import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ProgressRing } from '@/components/ui/progress-ring';
import {
  Calendar,
  Pill,
  Heart,
  Utensils,
  Droplets,
  Activity,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Dumbbell,
  Zap,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format, subDays, addDays, startOfDay, endOfDay, eachDayOfInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';

interface DayStats {
  date: Date;
  scheduledExecutions: number;
  adhocExecutions: number;
  executions: number;
  alerts: number;
  byType: Record<string, number>;
}

interface WeeklyStats {
  scheduledExecutions: number;
  adhocExecutions: number;
  totalExecutions: number;
  totalAlerts: number;
  totalShifts: number;
  totalScheduledInstances: number;
  avgPerDay: number;
  lateTasks: number;
  adhocOther: number;
  byType: Record<string, number>;
  dailyStats: DayStats[];
}

const taskTypeLabels: Record<string, string> = {
  fixed_medication: 'carePlan.fixedMeds',
  sporadic_medication: 'dashboard.sporadicMeds',
  vital_sign: 'carePlan.vitals',
  food: 'carePlan.food',
  hygiene: 'carePlan.hygiene',
  incident: 'dashboard.incidents',
  physical_activity: 'carePlan.physicalActivity',
  other: 'carePlan.otherTasks'
};

const taskTypeColors: Record<string, string> = {
  fixed_medication: 'hsl(var(--primary))',
  sporadic_medication: 'hsl(var(--sporadic))',
  vital_sign: 'hsl(var(--critical))',
  food: 'hsl(var(--success))',
  hygiene: 'hsl(var(--chart-1))',
  incident: 'hsl(var(--destructive))',
  physical_activity: 'hsl(var(--warning))',
  other: 'hsl(var(--chart-5))'
};

const taskTypeIcons: Record<string, React.ReactNode> = {
  fixed_medication: <Pill className="w-5 h-5" />,
  sporadic_medication: <Pill className="w-5 h-5" />,
  vital_sign: <Heart className="w-5 h-5" />,
  food: <Utensils className="w-5 h-5" />,
  hygiene: <Droplets className="w-5 h-5" />,
  incident: <AlertTriangle className="w-5 h-5 text-destructive" />,
  physical_activity: <Dumbbell className="w-5 h-5" />,
  other: <Activity className="w-5 h-5" />
};

export default function WeeklyReport() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [stats, setStats] = useState<WeeklyStats>({
    scheduledExecutions: 0,
    adhocExecutions: 0,
    totalExecutions: 0,
    totalAlerts: 0,
    totalShifts: 0,
    totalScheduledInstances: 0,
    avgPerDay: 0,
    lateTasks: 0,
    adhocOther: 0,
    byType: {},
    dailyStats: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);

  const endDate = endOfDay(subDays(new Date(), weekOffset * 7));
  const startDate = startOfDay(subDays(endDate, 6));
  const isCurrentWeek = weekOffset === 0;

  useEffect(() => {
    if (patientId) {
      fetchWeeklyData();
    }
  }, [patientId, weekOffset]);

  const fetchWeeklyData = async () => {
    setIsLoading(true);
    try {
      const weekStart = startOfDay(startDate).toISOString();
      const weekEnd = endOfDay(endDate).toISOString();

      // Fetch all task executions for the week
      const { data: executionsData } = await supabase
        .from('task_executions')
        .select('id, executed_at, is_alert, is_sporadic, task_id, adhoc_task_type')
        .eq('patient_id', patientId)
        .gte('executed_at', weekStart)
        .lte('executed_at', weekEnd);

      // Get task types
      const byType: Record<string, number> = {};
      let totalAlerts = 0;
      let scheduledCount = 0;
      let adhocCount = 0;
      let adhocOtherCount = 0;
      let tasksMap = new Map<string, string>();

      if (executionsData && executionsData.length > 0) {
        const taskIds = [...new Set(executionsData.map(e => e.task_id).filter(Boolean))] as string[];

        if (taskIds.length > 0) {
          const { data: tasks } = await supabase
            .from('care_plan_tasks')
            .select('id, task_type')
            .in('id', taskIds);

          tasksMap = new Map(tasks?.map(t => [t.id, t.task_type]) || []);
        }

        executionsData.forEach(exec => {
          const taskType = exec.task_id ? (tasksMap.get(exec.task_id) || 'other') : (exec.adhoc_task_type || 'other');
          byType[taskType] = (byType[taskType] || 0) + 1;
          if (exec.is_alert || exec.adhoc_task_type === 'incident') totalAlerts++;

          // Categorize by 7-KPI logic
          if (exec.task_id && !exec.is_sporadic) {
            scheduledCount++;
          } else if (exec.adhoc_task_type === 'sporadic_medication' || (exec.task_id && exec.is_sporadic)) {
            adhocCount++;
          } else if (!exec.task_id) {
            adhocOtherCount++;
          }
        });
      }

      // Calculate daily stats with category breakdown
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      const dailyStats: DayStats[] = days.map(date => {
        const dayStart = startOfDay(date);
        const dayEnd = endOfDay(date);

        const dayExecutions = executionsData?.filter(e => {
          const execDate = new Date(e.executed_at);
          return execDate >= dayStart && execDate <= dayEnd;
        }) || [];

        // Count by type for this day
        const dayByType: Record<string, number> = {};
        let dayScheduled = 0;
        let dayAdhoc = 0;

        dayExecutions.forEach(exec => {
          const taskType = exec.task_id ? (tasksMap.get(exec.task_id) || 'other') : 'other';
          dayByType[taskType] = (dayByType[taskType] || 0) + 1;

          if (exec.is_sporadic || !exec.task_id) {
            dayAdhoc++;
          } else {
            dayScheduled++;
          }
        });

        return {
          date,
          scheduledExecutions: dayScheduled,
          adhocExecutions: dayAdhoc,
          executions: dayExecutions.length,
          alerts: dayExecutions.filter(e => e.is_alert).length,
          byType: dayByType
        };
      });

      const { data: shiftsData } = await supabase
        .from('shifts')
        .select('id')
        .eq('patient_id', patientId)
        .gte('check_in_at', weekStart)
        .lte('check_in_at', weekEnd);

      // Count total scheduled task instances and late tasks for the week
      let totalScheduledInstances = 0;
      let lateTasks = 0;
      if (shiftsData && shiftsData.length > 0) {
        const shiftIds = shiftsData.map(s => s.id);
        const { data: instancesData } = await supabase
          .from('shift_task_instances')
          .select('id, status')
          .in('shift_id', shiftIds);

        if (instancesData) {
          totalScheduledInstances = instancesData.length;
          lateTasks = instancesData.filter(i => i.status === 'late').length;
        }
      }

      const totalExecutions = executionsData?.length || 0;

      setStats({
        scheduledExecutions: scheduledCount,
        adhocExecutions: adhocCount,
        totalExecutions,
        totalAlerts,
        totalShifts: shiftsData?.length || 0,
        totalScheduledInstances,
        avgPerDay: Math.round(scheduledCount / 7),
        lateTasks,
        adhocOther: adhocOtherCount,
        byType,
        dailyStats
      });

    } catch (error) {
      console.error('Error fetching weekly data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Transform daily stats for stacked bar chart
  const chartData = stats.dailyStats.map(day => ({
    day: format(day.date, 'EEE', { locale: ptBR }).charAt(0).toUpperCase() + format(day.date, 'EEE', { locale: ptBR }).slice(1, 3),
    fullDate: format(day.date, "d 'de' MMM", { locale: ptBR }),
    ...day.byType
  }));

  const chartConfig: Record<string, { label: string; color: string }> = {};
  Object.keys(taskTypeLabels).forEach(type => {
    chartConfig[type] = {
      label: t(taskTypeLabels[type]),
      color: taskTypeColors[type]
    };
  });

  // Get all unique task types from the data
  const allTaskTypes = new Set<string>();
  stats.dailyStats.forEach(day => {
    Object.keys(day.byType).forEach(type => allTaskTypes.add(type));
  });

  return (
    <AppLayout
      title={t('weeklyReport.title')}
      showBack
      onBack={() => navigate(`/app/family/${patientId}`)}
    >
      <div className="p-4 space-y-4 pb-24">
        {/* Date Range with Week Navigation */}
        <Card>
          <CardContent className="p-3">
            <div className="flex items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setWeekOffset(weekOffset + 1)}
                disabled={isLoading}
                aria-label="Semana anterior"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <div className="flex flex-col items-center gap-0.5 min-w-0">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-semibold text-sm text-center">
                    {format(startDate, "d 'de' MMM", { locale: ptBR })} - {format(endDate, "d 'de' MMM 'de' yyyy", { locale: ptBR })}
                  </span>
                </div>
                {!isCurrentWeek && (
                  <button
                    onClick={() => setWeekOffset(0)}
                    className="text-xs text-primary hover:underline"
                  >
                    Voltar para semana atual
                  </button>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setWeekOffset(Math.max(0, weekOffset - 1))}
                disabled={isLoading || isCurrentWeek}
                aria-label="Próxima semana"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <div className="flex items-center justify-center h-40">
            <CareOSLoader size="md" />
          </div>
        ) : (
          <>
            {/* Summary Stats - Standardized 7-KPI Layout */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              <Card className="text-center p-3">
                <p className="text-lg font-bold text-primary">{stats.totalScheduledInstances}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.plannedTasks')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className="text-lg font-bold text-primary">{stats.scheduledExecutions}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.executedTasks')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className="text-lg font-bold text-primary">
                  {stats.totalScheduledInstances > 0
                    ? Math.round((stats.scheduledExecutions / stats.totalScheduledInstances) * 100)
                    : 0}%
                </p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.completion')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className={`text-lg font-bold ${stats.adhocExecutions > 0 ? 'text-sporadic' : 'text-muted-foreground'}`}>{stats.adhocExecutions}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.sporadicMeds')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className={`text-lg font-bold ${stats.totalAlerts > 0 ? 'text-destructive' : 'text-muted-foreground'}`}>{stats.totalAlerts}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.incidents')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className={`text-lg font-bold ${stats.lateTasks > 0 ? 'text-warning' : 'text-muted-foreground'}`}>{stats.lateTasks}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.lateTasks')}</p>
              </Card>
              <Card className="text-center p-3">
                <p className="text-lg font-bold text-muted-foreground">{stats.adhocOther}</p>
                <p className="text-[12px] text-muted-foreground">{t('dashboard.adhocTasks')}</p>
              </Card>
            </div>

            {/* Stacked Bar Chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{t('weeklyReport.dailyActivity')}</CardTitle>
              </CardHeader>
              <CardContent className="px-2 pb-3">
                <ChartContainer config={chartConfig} className="h-64 w-full">
                  <BarChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="day"
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      tickMargin={4}
                      tickLine={false}
                      axisLine={false}
                    />
                    <YAxis
                      width={40}
                      tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
                      allowDecimals={false}
                      tickLine={false}
                      axisLine={false}
                    />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (active && payload && payload.length) {
                          const fullDate = payload[0]?.payload?.fullDate;
                          return (
                            <div className="bg-popover p-2 rounded-lg shadow-lg border text-sm">
                              <p className="font-medium mb-1">{fullDate}</p>
                              {payload.map((entry: any, index: number) => (
                                entry.value > 0 && (
                                  <div key={index} className="flex items-center gap-2">
                                    <div
                                      className="w-3 h-3 rounded"
                                      style={{ backgroundColor: entry.fill }}
                                    />
                                    <span>{t(taskTypeLabels[entry.dataKey]) || entry.dataKey}: {entry.value}</span>
                                  </div>
                                )
                              ))}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    {Array.from(allTaskTypes).map((type) => (
                      <Bar
                        key={type}
                        dataKey={type}
                        stackId="a"
                        fill={taskTypeColors[type] || 'hsl(var(--muted-foreground))'}
                        name={t(taskTypeLabels[type]) || type}
                      />
                    ))}
                  </BarChart>
                </ChartContainer>

                {/* Legend */}
                <div className="flex flex-wrap gap-3 mt-4 justify-center">
                  {Array.from(allTaskTypes).map(type => (
                    <div key={type} className="flex items-center gap-1 text-xs">
                      <div
                        className="w-3 h-3 rounded"
                        style={{ backgroundColor: taskTypeColors[type] || 'hsl(var(--muted-foreground))' }}
                      />
                      <span>{t(taskTypeLabels[type]) || type}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* By Type Breakdown - Pie Chart Refactor */}
            <Card>
              <CardHeader className="pb-2 text-center">
                <CardTitle className="text-base">{t('weeklyReport.byCategory')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row items-center justify-around gap-6 py-4">
                  {/* Pie Chart Component */}
                  <div className="h-[200px] w-full max-w-[200px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={Object.entries(stats.byType)
                            .filter(([_, count]) => count > 0)
                            .map(([type, count]) => ({
                              name: t(taskTypeLabels[type] || type),
                              value: count,
                              type
                            }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {Object.entries(stats.byType)
                            .filter(([_, count]) => count > 0)
                            .map(([type], index) => (
                              <Cell key={`cell-${index}`} fill={taskTypeColors[type] || 'hsl(var(--muted-foreground))'} />
                            ))}
                        </Pie>
                        <Tooltip
                          content={({ active, payload }) => {
                            if (active && payload && payload.length) {
                              const data = payload[0].payload;
                              return (
                                <div className="bg-popover p-2 rounded-lg shadow-lg border text-sm">
                                  <p className="font-medium">{data.name}: <span className="font-bold">{data.value}</span></p>
                                </div>
                              );
                            }
                            return null;
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Enhanced Legend with Details */}
                  <div className="flex-1 space-y-3 w-full">
                    {Object.entries(stats.byType)
                      .filter(([_, count]) => count > 0)
                      .sort((a, b) => b[1] - a[1]) // Sort by volume
                      .map(([type, count]) => {
                        const percentage = stats.totalExecutions > 0
                          ? Math.round((count / stats.totalExecutions) * 100)
                          : 0;

                        return (
                          <div key={type} className="flex items-center justify-between group">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-3 h-3 rounded-full shrink-0"
                                style={{ backgroundColor: taskTypeColors[type] || 'hsl(var(--muted-foreground))' }}
                              />
                              <span className="text-sm truncate max-w-[140px]">{t(taskTypeLabels[type] || type)}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <span className="font-bold">{count}</span>
                              <span className="text-xs text-muted-foreground tabular-nums">({percentage}%)</span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  );
}
